import { useState, useEffect } from 'react'

/**
 * Global dev offline flag — shared between useRealtime and useYjs
 * without prop drilling. Only used in development.
 *
 * Also tracks a recovery counter that increments on offline→online
 * transitions. Components use this to auto-retry failed fetches.
 */

type Listener = (offline: boolean) => void

let _offline = false
let _recoveryCount = 0
const _listeners = new Set<Listener>()
const _recoveryListeners = new Set<() => void>()

export const devOfflineState = {
  get offline() { return _offline },
  get recoveryCount() { return _recoveryCount },

  set(offline: boolean) {
    const wasOffline = _offline
    _offline = offline
    for (const fn of _listeners) fn(offline)

    // Offline → online transition: fire recovery event
    if (wasOffline && !offline) {
      _recoveryCount++
      for (const fn of _recoveryListeners) fn()
      // CustomEvent for useOnlineRecovery hooks (avoids module dedup issues)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('papan:online-recovery'))
      }
    }
  },

  subscribe(fn: Listener): () => void {
    _listeners.add(fn)
    return () => _listeners.delete(fn)
  },

  /** Subscribe to offline→online recovery events */
  onRecovery(fn: () => void): () => void {
    _recoveryListeners.add(fn)
    return () => _recoveryListeners.delete(fn)
  },
}

/**
 * React hook: returns a counter that increments on offline→online recovery.
 * Listens for:
 *   - 'papan:online-recovery' CustomEvent (from dev toggle — no module dedup issues)
 *   - browser 'online' event (real network recovery)
 * Add to useEffect deps to auto-retry failed fetches.
 */
export function useOnlineRecovery(): number {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const inc = () => setCount(n => n + 1)

    window.addEventListener('papan:online-recovery', inc)
    window.addEventListener('online', inc)

    return () => {
      window.removeEventListener('papan:online-recovery', inc)
      window.removeEventListener('online', inc)
    }
  }, [])

  return count
}
